import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { API_BASE_URL } from '../../config/api'
import { authFetch } from '../../utils/authFetch'
import {
  Building2,
  Plus,
  Search,
  Globe,
  AlertCircle,
  CheckCircle2,
  Clock,
  X,
  Archive,
  Loader2
} from 'lucide-react'
import CredentialModal from './CredentialModal'

const ADMIN_BRAND = {
  primary: '#4F46E5',
  primaryLight: '#EEF2FF',
  primaryText: '#3730A3',
  gradient: 'linear-gradient(to right, #2563EB, #4F46E5)',
}

interface Operator {
  id: string
  name: string
  email: string
  company: string
  plan: 'starter' | 'professional' | 'enterprise'
  status: 'active' | 'suspended' | 'archived'
  joinDate: string
  subdomain: string
  customDomain: string | null
  totalBookings: number
  monthlyRevenue: number
}

const generateSlug = (name: string): string => {
  return name.toLowerCase().trim()
    .replace(/[\s\.&,'+]+/g, '-')
    .replace(/[^a-z0-9\-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  suspended: 'bg-yellow-100 text-yellow-800',
  archived: 'bg-gray-100 text-gray-800'
}

const planColors: Record<string, { className?: string; style?: React.CSSProperties }> = {
  starter: { className: 'bg-blue-100 text-blue-800' },
  professional: { style: { backgroundColor: ADMIN_BRAND.primaryLight, color: ADMIN_BRAND.primaryText } },
  enterprise: { style: { backgroundColor: ADMIN_BRAND.primaryLight, color: ADMIN_BRAND.primaryText } },
}

const OperatorManagement: React.FC = () => {
  const [operators, setOperators] = useState<Operator[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [planFilter, setPlanFilter] = useState<string>('all')
  const [showArchived, setShowArchived] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const navigate = useNavigate()
  const [platformDomain, setPlatformDomain] = useState('booking.atlasiq.com')

  // Create modal state
  const [createForm, setCreateForm] = useState({
    company: '',
    email: '',
    plan: 'starter' as 'starter' | 'professional' | 'enterprise',
    slug: '',
    slugEditable: false
  })
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [slugStatus, setSlugStatus] = useState<{ checking: boolean; available: boolean | null; reason?: string }>({
    checking: false,
    available: null
  })
  const slugCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Credentials modal state
  const [showCredentialsModal, setShowCredentialsModal] = useState(false)
  const [credentials, setCredentials] = useState<{ email: string; password: string } | null>(null)
  const [emailQueued, setEmailQueued] = useState(false)

  const fetchOperators = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const url = `${API_BASE_URL}/api/super-admin/operators${showArchived ? '?include_archived=true' : ''}`
      const response = await authFetch(url)
      if (!response.ok) throw new Error('Failed to fetch operators')
      const data = await response.json()
      if (data.success) {
        setOperators(data.operators || [])
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load operators'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [showArchived])

  useEffect(() => {
    fetchOperators()
  }, [fetchOperators])

  // Fetch platform domain from config
  useEffect(() => {
    const fetchDomain = async () => {
      try {
        const res = await authFetch(`${API_BASE_URL}/api/super-admin/config/domain`)
        if (res.ok) {
          const data = await res.json()
          const domain = data?.config?.platform_domain
          if (domain) setPlatformDomain(domain)
        }
      } catch { /* keep default */ }
    }
    fetchDomain()
  }, [])

  // Auto-clear success messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  // Check subdomain availability (debounced)
  const checkSlugAvailability = useCallback(async (slug: string) => {
    if (!slug || slug.length < 3) {
      setSlugStatus({ checking: false, available: null })
      return
    }
    setSlugStatus({ checking: true, available: null })
    try {
      const response = await authFetch(`${API_BASE_URL}/api/super-admin/operators/check-subdomain/${slug}`)
      if (!response.ok) throw new Error('Check failed')
      const data = await response.json()
      setSlugStatus({
        checking: false,
        available: data.available,
        reason: data.reason || undefined
      })
    } catch {
      setSlugStatus({ checking: false, available: null })
    }
  }, [])

  const handleCompanyChange = (value: string) => {
    setCreateForm(prev => {
      const newSlug = prev.slugEditable ? prev.slug : generateSlug(value)
      // Trigger debounced slug check
      if (slugCheckTimer.current) clearTimeout(slugCheckTimer.current)
      if (!prev.slugEditable && newSlug.length >= 3) {
        slugCheckTimer.current = setTimeout(() => checkSlugAvailability(newSlug), 500)
      }
      return { ...prev, company: value, slug: newSlug }
    })
  }

  const handleSlugChange = (value: string) => {
    const cleaned = value.toLowerCase().replace(/[^a-z0-9\-]/g, '')
    setCreateForm(prev => ({ ...prev, slug: cleaned }))
    if (slugCheckTimer.current) clearTimeout(slugCheckTimer.current)
    if (cleaned.length >= 3) {
      slugCheckTimer.current = setTimeout(() => checkSlugAvailability(cleaned), 500)
    } else {
      setSlugStatus({ checking: false, available: null })
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!createForm.company.trim() || !createForm.email.trim()) return
    if (slugStatus.available === false) return

    setCreating(true)
    setCreateError(null)

    try {
      const response = await authFetch(`${API_BASE_URL}/api/super-admin/operators`, {
        method: 'POST',
        body: JSON.stringify({
          name: createForm.company.trim(),
          email: createForm.email.trim(),
          company: createForm.company.trim(),
          plan: createForm.plan,
          subdomain: createForm.slug || undefined
        })
      })

      if (response.status === 409) {
        const data = await response.json()
        setCreateError(data.detail || 'Subdomain conflict - please choose a different name')
        return
      }

      if (!response.ok) {
        const data = await response.json()
        setCreateError(data.detail || 'Failed to create operator')
        return
      }

      const data = await response.json()

      if (data.admin_credentials) {
        setCredentials({
          email: data.admin_credentials.email,
          password: data.admin_credentials.password
        })
        setEmailQueued(!!data.email_queued)
        setShowCredentialsModal(true)
      }

      setShowCreateModal(false)
      resetCreateForm()
      setSuccessMessage(`Operator "${createForm.company}" created successfully`)
      fetchOperators()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create operator'
      setCreateError(message)
    } finally {
      setCreating(false)
    }
  }

  const resetCreateForm = () => {
    setCreateForm({ company: '', email: '', plan: 'starter', slug: '', slugEditable: false })
    setCreateError(null)
    setSlugStatus({ checking: false, available: null })
  }

  // Client-side filtering
  const filteredOperators = operators.filter(operator => {
    const matchesSearch = searchTerm === '' ||
      operator.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (operator.subdomain || '').toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || operator.status === statusFilter
    const matchesPlan = planFilter === 'all' || operator.plan === planFilter
    return matchesSearch && matchesStatus && matchesPlan
  })

  // Stats
  const totalOperators = operators.length
  const activeCount = operators.filter(o => o.status === 'active').length
  const suspendedCount = operators.filter(o => o.status === 'suspended').length
  const archivedCount = operators.filter(o => o.status === 'archived').length

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading operators...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Building2 className="h-7 w-7 text-blue-600 mr-3" />
            B2B Operators
          </h2>
          <p className="text-sm text-gray-500 mt-1">Manage tour operators and travel agencies on the platform</p>
        </div>
        <button
          onClick={() => { resetCreateForm(); setShowCreateModal(true) }}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Operator
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
          <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
          <span className="text-sm text-green-700">{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)} className="ml-auto text-green-400 hover:text-green-600">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div className={`grid gap-4 ${showArchived ? 'grid-cols-4' : 'grid-cols-3'}`}>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-2xl font-bold text-gray-900">{totalOperators}</div>
          <div className="text-sm text-gray-500">Total Operators</div>
        </div>
        <div className="bg-white rounded-lg border border-green-200 p-4">
          <div className="text-2xl font-bold text-green-700">{activeCount}</div>
          <div className="text-sm text-green-600">Active</div>
        </div>
        <div className="bg-white rounded-lg border border-yellow-200 p-4">
          <div className="text-2xl font-bold text-yellow-700">{suspendedCount}</div>
          <div className="text-sm text-yellow-600">Suspended</div>
        </div>
        {showArchived && (
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-500">{archivedCount}</div>
            <div className="text-sm text-gray-400">Archived</div>
          </div>
        )}
      </div>

      {/* Filters Bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by company name or subdomain..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            {showArchived && <option value="archived">Archived</option>}
          </select>

          {/* Plan Filter */}
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Plans</option>
            <option value="starter">Starter</option>
            <option value="professional">Professional</option>
            <option value="enterprise">Enterprise</option>
          </select>

          {/* Show Archived Toggle */}
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
            <button
              onClick={() => setShowArchived(!showArchived)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                showArchived ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                  showArchived ? 'translate-x-4.5' : 'translate-x-0.5'
                }`}
              />
            </button>
            <Archive className="h-4 w-4" />
            Show Archived
          </label>
        </div>
      </div>

      {/* Operators Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {filteredOperators.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <Building2 className="h-12 w-12 text-gray-300 mb-4" />
            <p className="text-lg font-medium">No operators found</p>
            <p className="text-sm mt-1">
              {searchTerm || statusFilter !== 'all' || planFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first operator to get started'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Company</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Plan</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredOperators.map((operator) => (
                <tr
                  key={operator.id}
                  onClick={() => navigate(`/super-admin/operators/${operator.id}`)}
                  className="cursor-pointer hover:bg-blue-50 transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 text-sm">{operator.company}</div>
                    {operator.subdomain && (
                      <div className="text-xs text-gray-400 flex items-center mt-0.5">
                        <Globe className="h-3 w-3 mr-1" />
                        {operator.subdomain}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900">{operator.name}</div>
                    <div className="text-xs text-gray-400">{operator.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${planColors[operator.plan]?.className || (!planColors[operator.plan]?.style ? 'bg-gray-100 text-gray-800' : '')}`}
                      style={planColors[operator.plan]?.style}
                    >
                      {operator.plan}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${statusColors[operator.status] || 'bg-gray-100 text-gray-800'}`}>
                      {operator.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-500 flex items-center">
                      <Clock className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                      {operator.joinDate}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {filteredOperators.length > 0 && (
          <div className="bg-gray-50 border-t border-gray-200 px-4 py-2 text-xs text-gray-500">
            Showing {filteredOperators.length} of {operators.length} operators
          </div>
        )}
      </div>

      {/* Create Operator Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Create New Operator</h3>
              <button
                onClick={() => { setShowCreateModal(false); resetCreateForm() }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleCreate} className="p-6 space-y-5">
              {createError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
                  <AlertCircle className="h-4 w-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-red-700">{createError}</span>
                </div>
              )}

              {/* Company Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                <input
                  type="text"
                  value={createForm.company}
                  onChange={(e) => handleCompanyChange(e.target.value)}
                  placeholder="e.g., Acme Travel Agency"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Admin Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Email *</label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="admin@company.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Subdomain Section */}
              {createForm.company.trim().length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700 flex items-center">
                      <Globe className="h-4 w-4 mr-1.5 text-gray-500" />
                      Subdomain
                    </label>
                    <button
                      type="button"
                      onClick={() => setCreateForm(prev => ({ ...prev, slugEditable: !prev.slugEditable }))}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      {createForm.slugEditable ? 'Auto-generate' : 'Edit'}
                    </button>
                  </div>

                  {createForm.slugEditable ? (
                    <input
                      type="text"
                      value={createForm.slug}
                      onChange={(e) => handleSlugChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="custom-slug"
                    />
                  ) : (
                    <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-mono text-gray-700">
                      {createForm.slug || '...'}
                    </div>
                  )}

                  {/* Full URL Preview */}
                  {createForm.slug && (
                    <div className="text-xs text-gray-500">
                      <span className="font-medium">URL: </span>
                      <span className="font-mono">{createForm.slug}.{platformDomain}</span>
                    </div>
                  )}

                  {/* Availability Status */}
                  {createForm.slug && createForm.slug.length >= 3 && (
                    <div className="flex items-center text-xs">
                      {slugStatus.checking ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500 mr-1.5" />
                          <span className="text-blue-600">Checking availability...</span>
                        </>
                      ) : slugStatus.available === true ? (
                        <>
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-500 mr-1.5" />
                          <span className="text-green-600">Subdomain is available</span>
                        </>
                      ) : slugStatus.available === false ? (
                        <>
                          <AlertCircle className="h-3.5 w-3.5 text-red-500 mr-1.5" />
                          <span className="text-red-600">{slugStatus.reason || 'Subdomain is not available'}</span>
                        </>
                      ) : null}
                    </div>
                  )}
                </div>
              )}

              {/* Plan */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                <select
                  value={createForm.plan}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, plan: e.target.value as typeof prev.plan }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="starter">Starter</option>
                  <option value="professional">Professional</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); resetCreateForm() }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || slugStatus.available === false || slugStatus.checking}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creating ? (
                    <span className="flex items-center">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Creating...
                    </span>
                  ) : (
                    'Create Operator'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Credentials Modal */}
      {showCredentialsModal && (
        <CredentialModal
          credentials={credentials}
          emailQueued={emailQueued}
          isReset={false}
          onDismiss={() => {
            setShowCredentialsModal(false)
            setCredentials(null)
            setEmailQueued(false)
          }}
        />
      )}
    </div>
  )
}

export default OperatorManagement
